import { LitElement, html } from 'lit';

import { navigate } from '../../../lib/router/index.js';
import { api } from '../../api/index.js';
import { customerLoadState } from '../../components/customer-load-state.js';
import { assistantPath, assistantTelegramSecret, customerError, onlyAssistant, ownersOnly, workspaceName, workspacePath } from '../../i18n/customer-labels.js';
import { customerStyles } from '../../../styles/shared/customer.js';
import { formStyles } from '../../../styles/shared/forms.js';
import { pageStyles } from '../../../styles/shared/page.js';
import { customerTelegramPageStyles } from '../../../styles/pages/customer/telegram-page.js';

/** What BotFather gives: the bot id, a colon, the secret part. Checked here only to catch a bad paste. */
const BOT_TOKEN = /^\d{5,}:[A-Za-z0-9_-]{30,}$/;

/**
 * Connecting a Telegram bot, step by step: create it with BotFather, paste its token. The bot is
 * the one of an assistant (`assistant`, its id; left out, the only one of the workspace), whose
 * token is its own secret (TELEGRAM_BOT_TOKEN__<ID>): sealed by the plane at once, never read back.
 */
class TelegramPage extends LitElement {
  static properties = {
    name: { type: String },
    assistant: { type: String },
    loaded: { state: true },
    saving: { state: true },
    saveError: { state: true },
  };

  static styles = [
    pageStyles,
    formStyles,
    customerStyles,
    customerTelegramPageStyles,
  ];

  constructor() {
    super();
    this.loader = { value: undefined, error: null, refresh: () => this.load() };
    this.saving = false;
    this.saveError = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  async load() {
    this.loader = { ...this.loader, value: undefined, error: null };
    this.requestUpdate();
    try {
      this.loaded = await api.instance(this.name);
      if (this.loaded.desiredRevision) {
        // the name of the assistant and who can write to it are in its params
        const desired = await api.revision(this.name, this.loaded.desiredRevision.id);
        this.id = this.assistant ?? onlyAssistant(desired);
        const params = desired.params.assistants?.[this.id] ?? {};
        this.agentName = params.agentName;
        this.allowed = params.telegram?.allowFrom ?? [];
        this.acceptRequests = params.telegram?.acceptRequests === true;
        this.open = params.telegram?.public === true;
      }
      this.loader = { ...this.loader, value: this.loaded };
    } catch (error) {
      this.loader = { ...this.loader, error };
    }
    this.requestUpdate();
  }

  async save(event) {
    event.preventDefault();
    const token = event.target.elements.token.value.trim();
    if (!BOT_TOKEN.test(token)) {
      this.saveError = 'That does not look like a bot token: copy the whole line BotFather sent, numbers, colon and letters.';
      return;
    }
    this.saving = true;
    this.saveError = null;
    try {
      await api.setSecret(this.name, assistantTelegramSecret(this.id), token);
      navigate(workspacePath(this.name));
    } catch (error) {
      this.saveError = customerError(error);
    } finally {
      this.saving = false;
    }
  }

  render() {
    const pending = customerLoadState(this.loader);
    if (pending) {
      return html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/">Your workspaces</a></nav>${pending}`;
    }
    const name = this.agentName ?? 'Your assistant';
    const back = workspacePath(this.name);
    const edit = this.assistant ? `${assistantPath(this.name, this.assistant)}/edit` : `${back}/edit`;
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/">Your workspaces</a> / <a href=${back}>${workspaceName(this.loaded)}</a></nav>`;
    if (!this.loaded.canManage) {
      return html`${crumbs}<p class="empty">${ownersOnly(this.loaded)}</p>`;
    }
    if (!this.id) {
      // the address of the bot of the workspace when it has several assistants, or before it has one
      return html`${crumbs}<p class="empty">Choose the assistant to connect in <a href=${back}>its workspace</a>.</p>`;
    }
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1>Connect Telegram</h1>
          <p class="lead">${name} talks to people through a Telegram bot of yours. It takes two minutes.</p>
        </div>
      </header>
      <section class="card">
        <h2>1. Create the bot</h2>
        <ol>
          <li>Open Telegram and search for <strong>@BotFather</strong> (the one with the blue tick).</li>
          <li>Send it <code>/newbot</code>.</li>
          <li>Choose the name people see (for example “${name}”), then a username that ends in <code>bot</code>.</li>
          <li>BotFather answers with a token: a long line like <code>123456789:AAH…</code>. Copy it.</li>
        </ol>
      </section>
      <section class="card">
        <h2>2. Paste the token</h2>
        <form @submit=${(event) => this.save(event)}>
          <div class="field">
            <label for="token">Bot token</label>
            <span class="help">The long line BotFather sent you, numbers, colon and letters: paste all of it. Keep it private: whoever has it controls the bot.</span>
            <input id="token" name="token" type="password" autocomplete="off" required placeholder="123456789:AAH…" ?disabled=${this.saving} />
            <span class="help">It is encrypted as soon as it arrives: not even we can read it back. Paste it again to change bot.</span>
          </div>
          <div class="buttons">
            <button class="primary" type="submit" ?disabled=${this.saving}>${this.saving ? 'Connecting…' : 'Connect'}</button>
            <a class="button" href=${back}>Cancel</a>
            ${this.saveError ? html`<span class="form-error" role="alert">${this.saveError}</span>` : null}
          </div>
        </form>
      </section>
      ${this.renderStep3(back, edit)}
    `;
  }

  /** Who can write to the bot: open, by request (pairing), or only the people listed. */
  renderStep3(back, edit) {
    if (this.open) {
      return html`<p class="muted">The bot is open to everyone: anyone who finds it on Telegram can talk to it.</p>`;
    }
    if (this.acceptRequests) {
      return html`<div class="notice" role="status">
        <strong>3. Write to your bot</strong>Once connected, send your bot a message on Telegram: it answers with a code. You show up in
        <a href=${back}>the workspace</a>, with that code: allow yourself there, and the others who write to it the same way.
      </div>`;
    }
    return html`
      ${this.allowed?.length === 0
        ? html`<div class="notice" data-tone="warning" role="status">
            <strong>3. Choose who can write to it</strong>Nobody is on the list yet: until you add someone, the bot ignores every message, yours included. Add
            the Telegram user ids in <a href=${edit}>Edit</a>: a number, not a phone number. Or turn on <em>Accept requests</em> there: whoever writes to the
            bot gets a code, and you allow them from the workspace.
          </div>`
        : html`<p class="muted">
            Only the people you list in <a href=${edit}>Edit</a> get an answer. Telegram knows them by a user id, a number, not by their phone number.
          </p>`}
    `;
  }
}

customElements.define('plane-telegram-page', TelegramPage);
